import * as React from 'react';

import { enumPurchaseOrderStatus } from '@rematter/frontend-lib-api-client';
import {
  Button,
  cn,
  EntityDetails,
  LocalizedDate,
  Menu,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { MoreHorizontal } from 'lucide-react';

import { purchaseOrderCloseAtom } from '../../_graphql/mutationPurchaseOrderClose';
import { purchaseOrderOpenAtom } from '../../_graphql/mutationPurchaseOrderOpen';
import { purchaseOrderReopenAtom } from '../../_graphql/mutationPurchaseOrderReopen';
import { purchaseOrderVoidAtom } from '../../_graphql/mutationPurchaseOrderVoid';
import {
  $purchaseOrderDetailById,
  $purchaseOrderId,
} from '../../_state/purchaseOrderDetail.state';
import {
  DefaultTranslations,
  useTranslations,
} from '../../i18n/useTranslations';
import { tradeRoutePaths } from '../../routes';
import {
  PurchaseOrderDetailsTab,
  PurchaseOrderRecord,
} from '../../types/purchaseOrder';
import { ContractFreightSection } from '../_shared/ContractFreightSection';
import { PurchaseOrderStatusBadge } from '../_shared/StatusBadges';
import { LoadCreate } from '../loads/LoadCreate';

import { ClosePurchaseOrderDialog } from './ClosePurchaseOrderDialog';
import { usePurchaseOrderDetailMenuItems } from './hooks/usePurchaseOrderDetailMenuItems';
import { PurchaseOrderTabContractInfo } from './PurchaseOrderTabContractInfo';
import { PurchaseOrderTabLoads } from './PurchaseOrderTabLoads';
import { PurchaseOrderTabMaterials } from './PurchaseOrderTabMaterials';
import { PurchaseOrderTabNotes } from './PurchaseOrderTabNotes';
import { PurchaseOrderTabSupplier } from './PurchaseOrderTabSupplier';
import { VoidPurchaseOrderDialog } from './VoidPurchaseOrderDialog';

import { SalesRootLayout } from '~/modules/trade/_components/_layouts/SalesRootLayout';
import { useAtomFamilyCleanup } from '~/shared/hooks/useAtomFamilyCleanup';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const notificationBannerByPurchaseOrderStatus = ({
  purchaseOrder,
  t,
}: {
  purchaseOrder: PurchaseOrderRecord | null | undefined;
  t: DefaultTranslations;
}) => {
  if (!purchaseOrder) return null;

  if (purchaseOrder.status === enumPurchaseOrderStatus.Draft) {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.purchaseOrderDetail.notificationDraftBanner()}
        </div>
      ),
      type: 'neutral' as const,
    };
  }

  if (purchaseOrder.status === enumPurchaseOrderStatus.Closed) {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.purchaseOrderDetail.notificationBanner({
            status: (
              <span className="font-bold">
                {t.purchaseOrderDetail.statusLowercase.closed}
              </span>
            ),
            date: (
              <span className="font-bold">
                <LocalizedDate date={purchaseOrder.timestamps.updatedAt} />
              </span>
            ),
          })}
        </div>
      ),
      type: 'success' as const,
    };
  }

  if (purchaseOrder.status === enumPurchaseOrderStatus.Voided) {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.purchaseOrderDetail.notificationBanner({
            status: (
              <span className="font-bold">
                {t.purchaseOrderDetail.statusLowercase.voided}
              </span>
            ),
            date: (
              <span className="font-bold">
                <LocalizedDate date={purchaseOrder.timestamps.updatedAt} />
              </span>
            ),
          })}
        </div>
      ),
      type: 'failure' as const,
    };
  }

  return null;
};

export type PurchaseOrderDetailContentProps = {
  purchaseOrderId: string;
  variant?: 'default' | 'embedded';
  onBackPressed?: () => void;
  onRefetch?: () => void;
};

export const PurchaseOrderDetailContent: React.FC<
  PurchaseOrderDetailContentProps
> = ({ purchaseOrderId, variant = 'default', onBackPressed, onRefetch }) => {
  const t = useTranslations();
  const toast = useToast();

  const [tab, setTab] =
    React.useState<PurchaseOrderDetailsTab>('contract-info');
  const [isLoadDialogOpen, setIsLoadDialogOpen] = React.useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = React.useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = React.useState(false);

  useAtomFamilyCleanup([$purchaseOrderDetailById], purchaseOrderId);

  // CRITICAL: Use atomFamily-based atom - stable reference across renders
  const {
    data: purchaseOrder,
    isLoading,
    error,
    refetch,
  } = useAtomValue($purchaseOrderDetailById(purchaseOrderId));

  const openPurchaseOrder = useSetAtom(purchaseOrderOpenAtom({}));
  const closePurchaseOrder = useSetAtom(purchaseOrderCloseAtom({}));
  const voidPurchaseOrder = useSetAtom(purchaseOrderVoidAtom({}));
  const reopenPurchaseOrder = useSetAtom(purchaseOrderReopenAtom({}));

  const isDisabled = React.useMemo(
    () =>
      purchaseOrder?.status === enumPurchaseOrderStatus.Closed ||
      purchaseOrder?.status === enumPurchaseOrderStatus.Voided,
    [purchaseOrder?.status],
  );

  const canReopen = React.useMemo(
    () =>
      purchaseOrder?.status === enumPurchaseOrderStatus.Closed ||
      purchaseOrder?.status === enumPurchaseOrderStatus.Voided,
    [purchaseOrder?.status],
  );

  const notificationBanner = React.useMemo(
    () => notificationBannerByPurchaseOrderStatus({ purchaseOrder, t }),
    [purchaseOrder, t],
  );

  const tabs = React.useMemo(
    () => ({
      'contract-info': {
        title: t.purchaseOrderDetail.tabs.contractInfo,
        content: () =>
          purchaseOrder ? (
            <PurchaseOrderTabContractInfo
              purchaseOrder={purchaseOrder}
              disabled={isDisabled}
              variant={variant}
            />
          ) : null,
      },
      materials: {
        title: t.purchaseOrderDetail.tabs.materials,
        content: () => (
          <PurchaseOrderTabMaterials
            purchaseOrderId={purchaseOrderId}
            disabled={isDisabled}
            variant={variant}
          />
        ),
      },
      supplier: {
        title: t.purchaseOrderDetail.tabs.supplier,
        content: () =>
          purchaseOrder ? (
            <PurchaseOrderTabSupplier
              purchaseOrder={purchaseOrder}
              disabled={isDisabled}
              variant={variant}
            />
          ) : null,
      },
      loads: {
        title: t.purchaseOrderDetail.tabs.loads,
        content: () =>
          purchaseOrder ? (
            <PurchaseOrderTabLoads
              purchaseOrder={purchaseOrder}
              disableAddLoad={isDisabled}
              variant={variant}
            />
          ) : null,
      },
      freight: {
        title: t.purchaseOrderDetail.tabs.freight,
        content: () =>
          purchaseOrder ? (
            <ContractFreightSection
              entityType="purchaseOrder"
              entityId={purchaseOrderId}
              disabled={isDisabled}
            />
          ) : null,
      },
      notes: {
        title: t.purchaseOrderDetail.tabs.notes,
        content: () =>
          purchaseOrder ? (
            <PurchaseOrderTabNotes
              purchaseOrder={purchaseOrder}
              variant={variant}
            />
          ) : null,
      },
    }),
    [purchaseOrder, purchaseOrderId, isDisabled, variant, t],
  );

  const handleRefetch = React.useCallback(() => {
    refetch?.();
    onRefetch?.();
  }, [refetch, onRefetch]);

  const handleOpenPurchaseOrder = React.useCallback(async () => {
    if (!purchaseOrder?.id) return;
    try {
      await openPurchaseOrder({ id: purchaseOrder.id });
      toast.success(t.purchaseOrderDetail.messages.opened);
      handleRefetch();
    } catch (err) {
      toast.error(
        `Failed to open purchase order: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    }
  }, [purchaseOrder?.id, openPurchaseOrder, t, toast, handleRefetch]);

  const handleVoidConfirm = React.useCallback(
    async (purchaseOrderIdParam: string) => {
      try {
        await voidPurchaseOrder({ id: purchaseOrderIdParam });
        toast.success(t.purchaseOrderDetail.messages.voided);
        setIsVoidDialogOpen(false);
        handleRefetch();
      } catch (err) {
        toast.error(
          `Failed to void purchase order: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
        throw err;
      }
    },
    [voidPurchaseOrder, t, toast, handleRefetch],
  );

  const handleCloseConfirm = React.useCallback(
    async (purchaseOrderIdParam: string, notes?: string) => {
      try {
        await closePurchaseOrder({ id: purchaseOrderIdParam, notes });
        toast.success(t.purchaseOrderDetail.messages.closed);
        setIsCloseDialogOpen(false);
        handleRefetch();
      } catch (err) {
        toast.error(
          `Failed to close purchase order: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
      }
    },
    [closePurchaseOrder, t, toast, handleRefetch],
  );

  const handleReopen = React.useCallback(async () => {
    if (!purchaseOrder?.id) return;
    try {
      await reopenPurchaseOrder({ id: purchaseOrder.id });
      toast.success(t.purchaseOrderDetail.messages.reopened);
      handleRefetch();
    } catch (err) {
      toast.error(
        `Failed to reopen purchase order: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    }
  }, [purchaseOrder?.id, reopenPurchaseOrder, t, toast, handleRefetch]);

  const menuItems = usePurchaseOrderDetailMenuItems({
    purchaseOrder: purchaseOrder ?? null,
    setIsLoadDialogOpen,
    onRefetch: handleRefetch,
  });

  const purchaseOrderActions = React.useMemo(() => {
    if (!purchaseOrder) return null;

    return (
      <div className="flex items-center gap-2">
        <Menu
          trigger={
            <Button
              icon={<MoreHorizontal className="size-4" />}
              variant="outline"
              className="w-8"
            />
          }
          items={menuItems}
        />
        {purchaseOrder.status === enumPurchaseOrderStatus.Draft && (
          <Button
            variant="solid"
            color="primary"
            onPress={handleOpenPurchaseOrder}
          >
            {t.purchaseOrderDetail.actions.open}
          </Button>
        )}
        {purchaseOrder.status === enumPurchaseOrderStatus.Open && (
          <>
            <Button
              variant="outline"
              color="failure"
              onPress={() => setIsVoidDialogOpen(true)}
            >
              {t.purchaseOrderDetail.actions.void}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={() => setIsCloseDialogOpen(true)}
            >
              {t.purchaseOrderDetail.actions.close}
            </Button>
          </>
        )}
        {canReopen && (
          <Button variant="outline" onPress={handleReopen}>
            {t.purchaseOrderDetail.actions.reopen}
          </Button>
        )}
      </div>
    );
  }, [
    purchaseOrder,
    menuItems,
    handleOpenPurchaseOrder,
    handleReopen,
    canReopen,
    t,
  ]);

  const handleLoadDialogSuccess = React.useCallback(() => {
    setIsLoadDialogOpen(false);
    handleRefetch();
  }, [handleRefetch]);

  const handleCloseLoadDialog = React.useCallback(() => {
    setIsLoadDialogOpen(false);
  }, []);

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 w-1/4 rounded bg-neutral/20"></div>
          <div className="h-8 w-1/3 rounded bg-neutral/20"></div>
          <div className="h-32 rounded bg-neutral/20"></div>
          <div className="h-32 rounded bg-neutral/20"></div>
        </div>
      </div>
    );
  }

  if (error || !purchaseOrder) {
    return (
      <div className="p-4 text-failure">
        {t.purchaseOrderDetail.error(
          error?.message || 'Purchase order not found',
        )}
      </div>
    );
  }

  return (
    <>
      <EntityDetails
        className={cn(
          '@PurchaseOrderDetailsView',
          'relative overflow-hidden',
          variant === 'embedded' && 'rounded-lg',
        )}
        onChangeTab={(newTab) => setTab(newTab as PurchaseOrderDetailsTab)}
        currentTab={tab}
        onBackPressed={onBackPressed}
        primaryTitle={t.purchaseOrderDetail.title(purchaseOrder.documentNumber)}
        subtitle={
          <>
            {t.purchaseOrderDetail.created}{' '}
            <LocalizedDate date={purchaseOrder.timestamps.createdAt} />
          </>
        }
        badges={<PurchaseOrderStatusBadge status={purchaseOrder.status} />}
        headerActions={purchaseOrderActions}
        notificationBanner={notificationBanner}
        tabs={tabs}
      />
      <LoadCreate
        isOpen={isLoadDialogOpen}
        onClose={handleCloseLoadDialog}
        onSuccess={handleLoadDialogSuccess}
      />
      {isVoidDialogOpen && purchaseOrder && (
        <VoidPurchaseOrderDialog
          purchaseOrderId={purchaseOrder.id}
          onVoid={handleVoidConfirm}
          onCancel={() => setIsVoidDialogOpen(false)}
        />
      )}
      {isCloseDialogOpen && purchaseOrder && (
        <ClosePurchaseOrderDialog
          purchaseOrderId={purchaseOrder.id}
          onClose={handleCloseConfirm}
          onCancel={() => setIsCloseDialogOpen(false)}
        />
      )}
    </>
  );
};

export const PurchaseOrderDetailView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();
  const currentPurchaseOrderId = useAtomValue($purchaseOrderId);

  const handleBack = React.useCallback(() => {
    navigate(tradeRoutePaths.purchaseOrders);
  }, [navigate]);

  if (!currentPurchaseOrderId) {
    return (
      <SalesRootLayout>
        <div className="p-4 text-failure">
          {t.purchaseOrderDetail.error('Purchase order not found')}
        </div>
      </SalesRootLayout>
    );
  }

  return (
    <SalesRootLayout className="@PurchaseOrderDetailView">
      <PurchaseOrderDetailContent
        purchaseOrderId={currentPurchaseOrderId}
        onBackPressed={handleBack}
      />
    </SalesRootLayout>
  );
};
